import React from 'react';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { HyperlinkAddToolbar } from '@atlaskit/editor-common/link';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type {
	Command,
	ExtractInjectionAPI,
	FloatingToolbarItem,
	LinkPickerOptions,
} from '@atlaskit/editor-common/types';
import type { ContentNodeWithPos } from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { getParameters, setParameter } from '@atlaskit/native-embeds-common';

import type { EditorPluginNativeEmbedsPlugin } from '../../nativeEmbedsPluginType';
import { hideUrlToolbar } from '../commands/url';

interface EditUrlToolbarProps {
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined;
	linkPickerOptions?: LinkPickerOptions;
	lpLinkPicker: boolean;
	providerFactory: ProviderFactory;
	selectedNativeEmbed: ContentNodeWithPos;
	view: EditorView;
}

export const EditUrlToolbar = ({
	api,
	linkPickerOptions,
	lpLinkPicker,
	providerFactory,
	selectedNativeEmbed,
	view,
}: EditUrlToolbarProps): React.JSX.Element => {
	const currentUrl = getParameters(selectedNativeEmbed.node.attrs.parameters, 'url');
	const currentDisplayText = getParameters(
		selectedNativeEmbed.node.attrs.parameters,
		'displayText',
	);
	const localId = selectedNativeEmbed.node.attrs.localId as string | undefined;

	const onSubmit = (href: string, _title: string | undefined, displayText: string | undefined) => {
		hideUrlToolbar(view.state, view.dispatch);

		const urlChanged = href !== currentUrl;
		const displayTextChanged = displayText !== currentDisplayText;

		if ((urlChanged || displayTextChanged) && localId) {
			const extensionApi = api?.extension?.actions?.api();
			if (extensionApi?.doc?.update) {
				extensionApi.doc.update(localId, (current) => ({
					attrs: {
						...current.attrs,
						parameters: setParameter(current.attrs?.parameters, {
							url: href,
							displayText,
						}),
					},
				}));
			}
		}
	};

	return (
		<HyperlinkAddToolbar
			view={view}
			providerFactory={providerFactory}
			linkPickerOptions={linkPickerOptions}
			displayUrl={currentUrl}
			displayText={currentDisplayText}
			lpLinkPicker={lpLinkPicker}
			invokeMethod={INPUT_METHOD.FLOATING_TB}
			onSubmit={(href, title, displayText) => onSubmit(href, title, displayText)}
			onEscapeCallback={hideUrlToolbar}
			onClickAwayCallback={hideUrlToolbar}
		/>
	);
};

export const buildNativeEmbedEditUrlToolbar = (
	props: Omit<EditUrlToolbarProps, 'view'>,
): FloatingToolbarItem<Command> => ({
	type: 'custom',
	disableArrowNavigation: true,
	fallback: [],
	render: (view) =>
		view ? (
			<EditUrlToolbar
				api={props.api}
				linkPickerOptions={props.linkPickerOptions}
				lpLinkPicker={props.lpLinkPicker}
				providerFactory={props.providerFactory}
				selectedNativeEmbed={props.selectedNativeEmbed}
				view={view}
			/>
		) : null,
});
